import { ReactNode } from 'react';

import { BadgeCircle } from '@backrs_ui/common-ui/src/atoms/BadgeCircle';

import { Box, SxProps, Theme, Typography, Skeleton } from '@mui/material';
import ReactMarkdown from 'react-markdown';

export const BASE_NUGGET_HEIGHT = '367px';
export const BASE_NUGGET_WIDTH = '286px';
export const BASE_NUGGET_IMAGE_MAX_HEIGHT = '160px';

export type BaseNuggetProps = Readonly<{
  children: ReactNode;
  subText?: string;
  fitContent?: boolean;
  onClick?: () => void;
  isLoadingSubText?: boolean;
  forceSubtextAtBottom?: boolean;
  isHighlighted?: boolean;
}>;

export const BaseNugget = ({
  children,
  subText,
  fitContent = false,
  onClick,
  isLoadingSubText = false,
  forceSubtextAtBottom = false,
  isHighlighted = false,
}: BaseNuggetProps) => {
  return (
    <Box sx={containerSx(fitContent, forceSubtextAtBottom)} onClick={onClick}>
      <Box sx={subcontainerSx(fitContent, isHighlighted)}>{children}</Box>
      {(subText || (isLoadingSubText && subText !== '')) && (
        <Box sx={subTextContainerSx(fitContent, forceSubtextAtBottom)}>
          <Box mt={0.5}>
            <BadgeCircle loading={isLoadingSubText} size="md" />
          </Box>
          <Box sx={subTextSx}>
            {isLoadingSubText ? (
              <Box sx={subTextSkeletonSx}>
                <Skeleton variant="text" width="100%" height={20} />
                <Skeleton variant="text" width="80%" height={20} />
              </Box>
            ) : (
              <ReactMarkdown
                components={{
                  p: ({ children }) => (
                    <Typography component="p" variant="text-sm-regular" sx={{ margin: 0 }}>
                      {children}
                    </Typography>
                  ),
                }}
              >
                {subText}
              </ReactMarkdown>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const containerSx =
  (fitContent: boolean, forceSubtextAtBottom: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: fitContent || forceSubtextAtBottom ? 'fit-content' : BASE_NUGGET_HEIGHT,
    maxWidth: fitContent || forceSubtextAtBottom ? 'unset' : BASE_NUGGET_WIDTH,
    width: '100%',
    columnGap: backrs.spacing['spacing-3xl'],
    rowGap: backrs.spacing['spacing-md'],
    cursor: 'pointer',
    ...(fitContent &&
      !forceSubtextAtBottom && {
        '@media (min-width: 1100px)': {
          flexDirection: 'row',
          maxWidth: 'unset',
          width: '100%',
        },
      }),
  });

const subcontainerSx =
  (fitContent: boolean, isHighlighted: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: fitContent ? 'fit-content' : BASE_NUGGET_HEIGHT,
    borderRadius: backrs.radius['radius-4xl'],
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    border: `1px solid ${isHighlighted ? backrs.colors['Brand/300'] : backrs.colors['Colors/Base/Black']}`,
    padding: backrs.spacing['spacing-xl'],
    overflow: 'hidden',
    ...(isHighlighted && {
      outline: `2px solid ${backrs.colors['Brand/500']}`,
      outlineOffset: '2px',
    }),
  });

const subTextContainerSx =
  (fitContent: boolean, forceSubtextAtBottom: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: 'fit-content',
    gap: backrs.spacing['spacing-md'],
    px: backrs.spacing['spacing-xl'],
    py: backrs.spacing['spacing-md'],
    backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
    border: `1px solid ${backrs.colors['Gray/900']}`,
    alignItems: 'flex-start',
    width: '100%',
    borderRadius: `0 ${backrs.radius['radius-xl']} ${backrs.radius['radius-xl']} ${backrs.radius['radius-xl']}`,
    ...(fitContent &&
      !forceSubtextAtBottom && {
        '@media (min-width: 1300px)': {
          width: '50%',
        },
        '@media (min-width: 1450px)': {
          width: '75%',
        },
        '@media (min-width: 1600px)': {
          width: '100%',
        },
      }),
  });

const subTextSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  boxSizing: 'border-box',
  color: backrs.colors['Gray/900'],
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  lineHeight: 1.5,
});

const subTextSkeletonSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
});
